/**
 * Event page: "Lög æfingarinnar" — the songs to practise (ProgramList), "Opna fyrsta lag", and for
 * editors (the conductor, admins) "Velja lög" / "Breyta lögum" → ProgramEditor. Shown to everyone
 * when the event has songs; to editors always (so they can add some).
 */
import { ListMusic, Pencil, Play } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { EventDTO } from '../../shared/dto/events';
import { Button, Card, Spinner } from '../components/ui';
import { useT } from '../i18n';
import { orderedEntries } from '../viewer/setlist';
import { hasProgram, practiceSongHref } from './model';
import { usePracticeSongs } from './PracticeMenu';
import { ProgramEditor } from './ProgramEditor';
import { ProgramList } from './ProgramList';

export function ProgramCard({ ev, editor }: { ev: EventDTO; editor: boolean }) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const has = hasProgram(ev);
  if (!has && !editor) return null;
  return (
    <Card as="section" className="flex flex-col gap-3 p-5 md:p-6" >
      <div className="flex items-center gap-3">
        <ListMusic className="size-6 shrink-0 text-[var(--accent)]" aria-hidden />
        <h2 className="min-w-0 flex-1 text-[20px] font-bold">{t('practice.songs_title')}</h2>
        {editor ? (
          <Button variant="secondary" size="sm" icon={Pencil} className="min-h-11" onClick={() => setEditing(true)}>
            {has ? t('practice.edit') : t('practice.choose')}
          </Button>
        ) : null}
      </div>
      {has ? <ProgramSongs ev={ev} /> : <p className="text-[var(--muted)]">{t('practice.empty_editor')}</p>}
      {editor ? <ProgramEditor ev={ev} open={editing} onClose={() => setEditing(false)} /> : null}
    </Card>
  );
}

function ProgramSongs({ ev }: { ev: EventDTO }) {
  const t = useT();
  const navigate = useNavigate();
  const { list, songs, loading } = usePracticeSongs(ev.id);
  if (!list) return loading ? <Spinner label={t('common.loading')} /> : null;
  const first = orderedEntries(list.entries)[0];
  if (!first) return <p className="text-[var(--muted)]">{t('practice.nothing_visible')}</p>;
  return (
    <>
      <p className="text-[15px] text-[var(--muted)]">{t('practice.count', { count: list.entries.length })}</p>
      <ProgramList eventId={ev.id} list={list} songs={songs} />
      <Button icon={Play} block onClick={() => navigate(practiceSongHref(ev.id, 0, first))}>
        {t('practice.open_first')}
      </Button>
    </>
  );
}
